import type { Tenant } from '../lib/database.types';
import { formatPrice } from '../lib/cart';
import NpsForm from './NpsForm';

/**
 * Page de confirmation / « merci » post-commande (parité C6 — legacy `/merci`).
 * Deux usages :
 *  - en-app, juste après la commande (toutes les infos sont passées en props) ;
 *  - autonome via la route `/r/<slug>/merci?order=NUM` (retour de paiement Stripe) :
 *    on lit le n° de commande dans l'URL, l'état « payé » est confirmé par le webhook.
 */
interface ConfirmationProps {
  tenant: Tenant;
  orderNumber?: string | null;
  totalCents?: number | null;
  currency?: 'EUR' | 'CHF';
  pickupTime?: string | null;
  mode?: 'pickup' | 'delivery';
  paymentMethod?: 'online' | 'onsite' | null;
  /** Paiement confirmé (retour Stripe réussi). */
  paid?: boolean;
  /** Entitlement `reviews` (collecte NPS / avis Google). Défaut true = rétro-compatible. */
  reviewsEnabled?: boolean;
  onBackToMenu?: () => void;
}

function orderFromUrl(): string | null {
  try {
    const p = new URLSearchParams(window.location.search).get('order');
    return p && p.trim() ? p.trim() : null;
  } catch {
    return null;
  }
}

export default function Confirmation({
  tenant,
  orderNumber,
  totalCents,
  currency = 'EUR',
  pickupTime,
  mode,
  paymentMethod,
  paid,
  reviewsEnabled = true,
  onBackToMenu,
}: ConfirmationProps) {
  const num = orderNumber ?? orderFromUrl();
  const prep = tenant.prep_time_min ?? 15;

  const paymentNote = paid
    ? '✅ Paiement reçu, merci !'
    : paymentMethod === 'online'
      ? 'Votre paiement est en cours de confirmation.'
      : paymentMethod === 'onsite'
        ? '💵 À régler sur place au retrait.'
        : null;

  return (
    <section className="mx-auto flex w-full max-w-md flex-col items-center gap-4 px-4 py-8 text-center">
      <div
        className="flex h-16 w-16 items-center justify-center rounded-full text-3xl text-white"
        style={{ background: 'var(--brand-primary)' }}
      >
        ✓
      </div>
      <h2 className="text-2xl font-bold text-brand-primary">Merci pour votre commande !</h2>

      {num && (
        <p className="text-lg">
          Commande <span className="font-bold">#{num}</span> bien enregistrée.
        </p>
      )}

      <p className="opacity-70">
        {mode === 'delivery'
          ? 'Votre commande sera préparée puis livrée.'
          : `Votre commande sera prête au comptoir dans environ ${prep} min.`}
        {pickupTime ? ` Créneau : ${pickupTime}.` : ''}
      </p>

      {typeof totalCents === 'number' && totalCents > 0 && (
        <div className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-4 py-3 font-semibold">
          <span>Total</span>
          <span>{formatPrice(totalCents, currency)}</span>
        </div>
      )}

      {paymentNote && <p className="text-sm opacity-80">{paymentNote}</p>}

      {reviewsEnabled && (
        <div className="mt-2 w-full">
          <NpsForm tenantId={tenant.id} />
        </div>
      )}

      {onBackToMenu ? (
        <button
          onClick={onBackToMenu}
          className="mt-2 rounded-xl px-6 py-3 font-semibold text-white"
          style={{ background: 'var(--brand-primary)' }}
        >
          Retour à l'accueil
        </button>
      ) : (
        <a
          href={`/r/${tenant.slug}`}
          className="mt-2 rounded-xl px-6 py-3 font-semibold text-white"
          style={{ background: 'var(--brand-primary)' }}
        >
          Retour à l'accueil
        </a>
      )}
    </section>
  );
}
